import { useState, useEffect } from 'react';
import { HudFrame } from './HudFrame';
import { resolveRouteZoneReadability, type RouteZoneReadability } from '../shared/routeReadability';
import { resolveHudColorModeTokens, type HudColorMode } from './hudDesign';
import type { CtfMacroSummary } from '../state/useGameStore';
import type { ColorMode as ColorModeAlias } from '../game/cameraSettings';

type Vec3Like = {
  x?: number;
  y?: number;
  z?: number;
};

type HudPlayerSnapshot = {
  id?: string;
  displayName?: string;
  team?: string | null;
  position?: Vec3Like;
  yaw?: number;
  carryingFlagTeam?: string | null;
};

type HudFlagSnapshot = {
  team?: string | null;
  position?: Vec3Like;
  homePosition?: Vec3Like;
  holderId?: string | null;
  state?: string | null;
};

type HudBoostPadSnapshot = {
  id?: string;
  position?: Vec3Like;
  radius?: number;
  active?: boolean;
  cooldownSeconds?: number | null;
};

type HudInventoryStationSnapshot = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  radius?: number | null;
  active?: boolean | null;
  powered?: boolean | null;
};

type HudVehicleSnapshot = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  active?: boolean | null;
  vehicleId?: string | null;
  occupiedBy?: string | null;
  returnSeconds?: number | null;
};

type HudDeployableSnapshot = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  active?: boolean | null;
  deployableId?: string | null;
  ttlSeconds?: number | null;
};

type HudTacticalPingSnapshot = {
  id?: string;
  team?: string | null;
  position?: Vec3Like;
  ttlSeconds?: number;
  kind?: string | null;
  label?: string | null;
};

type HudRouteCaptureZone = {
  id?: string;
  position?: Vec3Like;
  radius?: number;
  ownerTeam?: string | null;
  captureTargetTeam?: string | null;
  captureProgress?: number;
  contested?: boolean;
  carrierInside?: boolean;
  scoringMultiplier?: number;
};

type HudObjectiveScoringHint = {
  id?: string;
  routeZoneId?: string | null;
  team?: string | null;
  kind?: string | null;
  message?: string | null;
  points?: number;
  multiplier?: number;
  ttlSeconds?: number;
  flagTeam?: string | null;
};

type BaseStateSnapshot = {
  generatorOnline?: boolean;
  generatorHealth?: number;
  sensorOnline?: boolean;
};

type ColorMode = ColorModeAlias | HudColorMode;

type RadarProps = {
  localPlayer: HudPlayerSnapshot;
  remotePlayers: Record<string, HudPlayerSnapshot>;
  flags: HudFlagSnapshot[];
  boostPads: HudBoostPadSnapshot[];
  inventoryStations?: HudInventoryStationSnapshot[];
  vehicles?: HudVehicleSnapshot[];
  deployables?: HudDeployableSnapshot[];
  tacticalPings?: HudTacticalPingSnapshot[];
  routeCaptureZones?: HudRouteCaptureZone[];
  objectiveScoringHints?: HudObjectiveScoringHint[];
  ctfMacro?: CtfMacroSummary | null;
  baseState?: BaseStateSnapshot | null;
  radarRange?: number;
  sensorBoost?: boolean;
  compact?: boolean;
  narrow?: boolean;
  minimal?: boolean;
  reducedMotion?: boolean;
  colorMode?: ColorMode;
};

type RadarPoint = {
  id: string;
  x: number;
  y: number;
  kind:
    | 'ally'
    | 'enemy'
    | 'self'
    | 'flag'
    | 'base'
    | 'boost'
    | 'carrier'
    | 'route'
    | 'ping'
    | 'chaser'
    | 'generator-status'
    | 'inventory'
    | 'vehicle'
    | 'deployable';
  color: string;
  size: number;
  stroke?: string;
  distance?: number;
  detail?: string;
  label?: string;
  ttlSeconds?: number;
  threatPriority: number;
  isAirborne?: boolean;
};

type RouteOverlay = {
  id: string;
  x: number;
  y: number;
  radiusPct: number;
  captureProgress: number;
  carrierInside: boolean;
  scoringMultiplier: number;
  highlighted: boolean;
  semantic: RouteZoneReadability;
  cueMessage: string | null;
};

type TacticalCue = {
  id: string;
  priority: number;
  tone: string;
  text: string;
};

const RADAR_RADIUS = 220;
const RADAR_SCALE_PCT = 42;
const RADAR_ENTITY_RANGE = 80;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function vecDelta(from: Vec3Like | undefined, to: Vec3Like | undefined) {
  return {
    x: (to?.x ?? 0) - (from?.x ?? 0),
    z: (to?.z ?? 0) - (from?.z ?? 0),
  };
}

function rotateLocal(dx: number, dz: number, yaw: number) {
  const angle = -(yaw ?? 0);
  const sin = Math.sin(angle);
  const cos = Math.cos(angle);
  return {
    x: dx * cos - dz * sin,
    y: dx * sin + dz * cos,
  };
}

function teamColor(team: string | null | undefined, colorMode: ColorMode = 'default') {
  const colors =
    colorMode === 'default'
      ? { blue: '#62ddff', red: '#ff6f6f' }
      : resolveHudColorModeTokens(colorMode);
  const key = (team ?? '').toString().toLowerCase();
  if (key === 'red' || key === '1' || key === 'blood') return colors.red;
  if (key === 'blue' || key === '0' || key === 'diamond') return colors.blue;
  return '#b7c7d8';
}

function localRelativePoint(
  origin: Vec3Like | undefined,
  yaw: number,
  position: Vec3Like | undefined,
  scale: number,
) {
  if (!position) {
    return null;
  }

  const delta = vecDelta(origin, position);
  const rotated = rotateLocal(delta.x, delta.z, yaw);
  const x = clamp(rotated.x / scale, -1, 1);
  const y = clamp(-rotated.y / scale, -1, 1);
  const distance = Math.hypot(delta.x, delta.z);
  return { x, y, distance };
}

function resolveFlagPosition(flag: HudFlagSnapshot) {
  return flag.position ?? flag.homePosition ?? null;
}

function formatDistance(distance?: number) {
  if (distance == null || !Number.isFinite(distance)) {
    return '';
  }

  const rounded = Math.round(distance);
  if (rounded >= 1000) {
    return `${(rounded / 1000).toFixed(1)}k`;
  }

  return `${rounded}m`;
}

function pointClip(kind: RadarPoint['kind']) {
  if (kind === 'self') {
    return 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)';
  }

  if (kind === 'base') {
    return 'polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)';
  }

  if (kind === 'boost') {
    return 'polygon(25% 0, 75% 0, 100% 50%, 75% 100%, 25% 100%, 0 50%)';
  }

  if (kind === 'ping') {
    return 'polygon(50% 0, 92% 28%, 92% 72%, 50% 100%, 8% 72%, 8% 28%)';
  }

  if (kind === 'vehicle') {
    return 'polygon(50% 0, 100% 38%, 82% 100%, 18% 100%, 0 38%)';
  }

  if (kind === 'deployable') {
    return 'polygon(50% 0, 88% 18%, 100% 58%, 72% 100%, 28% 100%, 0 58%, 12% 18%)';
  }

  if (kind === 'route') {
    return 'polygon(50% 0, 90% 22%, 100% 50%, 90% 78%, 50% 100%, 10% 78%, 0 50%, 10% 22%)';
  }

  if (kind === 'generator-status') {
    return 'polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)';
  }

  if (kind === 'inventory') {
    return 'polygon(50% 0, 88% 18%, 100% 50%, 88% 82%, 50% 100%, 12% 82%, 0 50%, 12% 18%)';
  }

  if (kind === 'chaser') {
    return 'polygon(20% 0, 80% 0, 100% 30%, 100% 70%, 80% 100%, 20% 100%, 0 70%, 0 30%)';
  }

  return 'polygon(20% 0, 80% 0, 100% 20%, 100% 80%, 80% 100%, 20% 100%, 0 80%, 0 20%)';
}

function resolveRadarLegend(colorMode: ColorMode): Array<{ kind: RadarPoint['kind']; tone: string; label: string }> {
  const colors = resolveHudColorModeTokens(colorMode);
  const allyTone = colorMode === 'default' ? '#62ddff' : colors.blue;
  const enemyTone = colorMode === 'default' ? '#ff9b71' : colors.red;
  return [
  { kind: 'self', tone: '#f4fbff', label: 'You' },
  { kind: 'ally', tone: allyTone, label: 'Ally' },
  { kind: 'enemy', tone: enemyTone, label: 'Enemy' },
  { kind: 'carrier', tone: '#ffcc66', label: 'Carrier / Flag' },
  { kind: 'chaser', tone: '#ff4444', label: 'Chaser' },
  { kind: 'boost', tone: '#8ef0c9', label: 'Boost' },
  { kind: 'ping', tone: '#d3a7ff', label: 'Ping' },
  { kind: 'vehicle', tone: '#ffcc66', label: 'Vehicle' },
  { kind: 'deployable', tone: '#b58cff', label: 'Deploy' },
  { kind: 'flag', tone: '#ffd166', label: 'Flag' },
  { kind: 'base', tone: '#9fb4c7', label: 'Base' },
  { kind: 'generator-status', tone: '#8ef0c9', label: 'Generator' },
  { kind: 'inventory', tone: '#8ef0c9', label: 'Inventory' },
  ];
}

function threatPriority(point: { kind: RadarPoint['kind']; carryingFlagTeam?: string | null; isEnemyCarrier?: boolean }): number {
  if (point.kind === 'carrier' && point.isEnemyCarrier) {
    return 100;
  }
  if (point.kind === 'carrier') {
    return 80;
  }
  if (point.kind === 'chaser') {
    return 90;
  }
  if (point.kind === 'enemy') {
    return 60;
  }
  if (point.kind === 'vehicle') {
    return 58;
  }
  if (point.kind === 'deployable') {
    return 48;
  }
  if (point.kind === 'flag') {
    return 50;
  }
  if (point.kind === 'self') {
    return 40;
  }
  if (point.kind === 'ally') {
    return 30;
  }
  if (point.kind === 'base' || point.kind === 'generator-status') {
    return 20;
  }
  if (point.kind === 'inventory') {
    return 18;
  }
  if (point.kind === 'boost') {
    return 15;
  }
  if (point.kind === 'ping') {
    return 10;
  }
  if (point.kind === 'route') {
    return 5;
  }
  return 0;
}

function contactSummary(points: RadarPoint[]) {
  return points.reduce(
    (summary, point) => {
      if (point.kind === 'self' || point.kind === 'base' || point.kind === 'generator-status') {
        return summary;
      }
      if (point.kind === 'ally') {
        summary.allies += 1;
      }
      if (point.kind === 'enemy') {
        summary.enemies += 1;
      }
      if (point.kind === 'carrier' || point.kind === 'flag') {
        summary.carriers += 1;
      }
      if (point.kind === 'ping') {
        summary.pings += 1;
      }
      if (point.kind === 'vehicle') {
        summary.vehicles += 1;
      }
      if (point.kind === 'deployable') {
        summary.deployables += 1;
      }
      if (point.kind === 'inventory') {
        summary.inventory += 1;
      }
      return summary;
    },
    { allies: 0, enemies: 0, carriers: 0, pings: 0, vehicles: 0, deployables: 0, inventory: 0 },
  );
}

function frameClip() {
  return 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))';
}

function normalizeText(value?: string | null, maxLength = 18) {
  const cleaned = (value ?? '').trim();
  if (!cleaned) {
    return null;
  }

  if (cleaned.length <= maxLength) {
    return cleaned;
  }

  return `${cleaned.slice(0, Math.max(8, maxLength - 1))}…`;
}

function vehicleRadarName(vehicleId?: string | null) {
  const cleaned = (vehicleId ?? 'vehicle').replace(/[_-]+/g, ' ').trim();
  return cleaned ? cleaned.toUpperCase() : 'VEHICLE';
}

function routeOverlayQualifier(overlay: {
  carrierInside: boolean;
  scoringMultiplier: number;
}) {
  const tags: string[] = [];
  if (overlay.carrierInside) {
    tags.push('CARRIER');
  }

  if (overlay.scoringMultiplier > 1.01) {
    tags.push(`BONUS x${overlay.scoringMultiplier.toFixed(1)}`);
  }

  return tags.length > 0 ? tags.join(' · ') : null;
}

function routeOverlayLabel(semantic: RouteZoneReadability, overlay: { carrierInside: boolean; scoringMultiplier: number }) {
  const qualifier = routeOverlayQualifier(overlay);
  return qualifier ? `${semantic.statusLabel} · ${qualifier}` : semantic.statusLabel;
}

function routeOverlayTone(semantic: RouteZoneReadability, overlay: { carrierInside: boolean; scoringMultiplier: number }) {
  if (semantic.status === 'contested') {
    return '#ffcf6d';
  }

  if (overlay.carrierInside || overlay.scoringMultiplier > 1.01) {
    return '#ffcc66';
  }

  if (semantic.status === 'threat') {
    return semantic.ringColor;
  }

  return semantic.status === 'neutral' ? '#9fb4c7' : semantic.ringColor;
}

function routeOverlaySize(semantic: RouteZoneReadability, overlay: { carrierInside: boolean; scoringMultiplier: number }) {
  const pressureBoost = overlay.carrierInside || overlay.scoringMultiplier > 1.01 ? 1 : 0;

  if (semantic.status === 'contested') {
    return 8 + pressureBoost;
  }

  if (semantic.status === 'threat') {
    return 7 + pressureBoost;
  }

  if (semantic.status === 'hold') {
    return 6 + pressureBoost;
  }

  return 5;
}

function routeOverlayBadge(semantic: RouteZoneReadability, overlay: { carrierInside: boolean; scoringMultiplier: number }) {
  if (semantic.status === 'contested') {
    return '!';
  }

  if (overlay.carrierInside) {
    return 'C';
  }

  if (overlay.scoringMultiplier > 1.01) {
    return '+';
  }

  if (semantic.status === 'threat') {
    return 'P';
  }

  if (semantic.status === 'hold') {
    return 'H';
  }

  return '';
}

function routeOverlayPriority(semantic: RouteZoneReadability, overlay: { carrierInside: boolean; scoringMultiplier: number; captureProgress: number; highlighted: boolean }) {
  const statusWeight =
    semantic.status === 'contested' ? 5 : semantic.status === 'threat' ? 4 : semantic.status === 'hold' ? 3 : 1;
  const pressureWeight = (overlay.carrierInside ? 32 : 0) + Math.max(0, Math.min(18, (overlay.scoringMultiplier - 1) * 28));
  const hintWeight = overlay.highlighted ? 24 : 0;

  return statusWeight * 100 + pressureWeight + hintWeight + overlay.captureProgress * 24;
}

function hintTone(kind?: string | null, team?: string | null) {
  const normalizedKind = (kind ?? '').toLowerCase();
  if (normalizedKind === 'capture' || normalizedKind === 'attack') {
    return team === 'blue' ? '#62ddff' : '#ff6f6f';
  }

  if (normalizedKind === 'defend' || normalizedKind === 'defense' || normalizedKind === 'return') {
    return '#ffcc66';
  }

  if (normalizedKind === 'base' || normalizedKind === 'carrierstop') {
    return '#ff9b71';
  }

  if (normalizedKind === 'route') {
    return '#b58cff';
  }

  return '#9fb4c7';
}

function hintPriority(hint: HudObjectiveScoringHint) {
  const kind = (hint.kind ?? '').toLowerCase();
  const kindRank = kind === 'capture' ? 4 : kind === 'defense' || kind === 'base' || kind === 'route' ? 3 : kind === 'return' ? 2 : 1;
  return kindRank * 100 + (hint.multiplier ?? 1) * 12 + (hint.points ?? 0) + (hint.ttlSeconds ?? 0) * 2;
}

function normalizeTeam(team?: string | null): 'red' | 'blue' | null {
  const normalized = (team ?? '').toLowerCase();
  if (normalized === 'red' || normalized === 'blue') {
    return normalized;
  }

  return null;
}

function routePriority(overlay: RouteOverlay) {
  return routeOverlayPriority(overlay.semantic, overlay);
}

function resolvePingSemantic(kind?: string | null) {
  const key = (kind ?? '').toLowerCase();
  if (key === 'danger' || key === 'need-help' || key === 'route-danger') {
    return { tone: '#ff9b71', label: '!' };
  }
  if (key === 'enemy' || key === 'flag' || key === 'enemy-spotted') {
    return { tone: '#ffcc66', label: 'E' };
  }
  if (key === 'defend' || key === 'generator') {
    return { tone: '#62ddff', label: 'D' };
  }
  if (key === 'move' || key === 'route' || key === 'route-safe') {
    return { tone: '#b58cff', label: 'M' };
  }
  if (key === 'need-repair') {
    return { tone: '#8ef0c9', label: 'R' };
  }
  if (key === 'vehicle-here') {
    return { tone: '#62ddff', label: 'V' };
  }

  return { tone: '#d3a7ff', label: '!' };
}

function pingPriority(ping: HudTacticalPingSnapshot) {
  const key = (ping.kind ?? '').toLowerCase();
  const rank =
    key === 'danger' || key === 'need-help' || key === 'route-danger'
      ? 5
      : key === 'enemy' || key === 'flag' || key === 'enemy-spotted'
        ? 4
        : key === 'defend' || key === 'generator'
          ? 3
          : key === 'move' || key === 'route' || key === 'route-safe'
            ? 2
            : 1;
  return rank * 100 + (ping.ttlSeconds ?? 0) * 8;
}

function shortPingCue(ping: HudTacticalPingSnapshot, distance?: number) {
  const kind = (ping.kind ?? '').trim().toUpperCase();
  const label = normalizeText(ping.label, 16)?.toUpperCase();
  const dist = formatDistance(distance);
  const ttl = ping.ttlSeconds != null && ping.ttlSeconds > 0 ? `${Math.max(0, Math.ceil(ping.ttlSeconds))}S` : null;
  const segments = [kind || 'PING', label, dist, ttl].filter((part): part is string => Boolean(part));
  return segments.slice(0, 3).join(' · ');
}

function routeStatusText(overlay: RouteOverlay) {
  return routeOverlayLabel(overlay.semantic, overlay);
}

function shouldRenderRouteText(overlay: RouteOverlay) {
  return overlay.highlighted || overlay.semantic.status !== 'neutral';
}

function ctfMacroCueText(macro: CtfMacroSummary) {
  return `${macro.title} · ${macro.cue}`;
}

function shouldPromoteContactCue(point: RadarPoint) {
  return Boolean(point.detail) && point.kind !== 'self' && point.kind !== 'base' && point.kind !== 'boost' && point.kind !== 'route' && point.kind !== 'generator-status';
}

export { RADAR_ENTITY_RANGE, threatPriority };

export function Radar({
  localPlayer,
  remotePlayers,
  flags,
  boostPads,
  inventoryStations = [],
  vehicles = [],
  deployables = [],
  tacticalPings = [],
  routeCaptureZones = [],
  objectiveScoringHints = [],
  ctfMacro = null,
  baseState = null,
  radarRange = RADAR_ENTITY_RANGE,
  sensorBoost = false,
  compact = false,
  narrow = false,
  minimal = false,
  reducedMotion = false,
  colorMode = 'default',
}: RadarProps) {
  const localPosition = localPlayer.position;
  const localYaw = localPlayer.yaw ?? 0;
  const localTeam = localPlayer.team ?? null;
  const headingDegrees = Math.round(((localYaw / (Math.PI * 2)) * 360 + 360) % 360);
  const points: RadarPoint[] = [];
  const radarTone = resolveHudColorModeTokens(colorMode);
  const allyColor = teamColor('blue', colorMode);
  const enemyColor = teamColor('red', colorMode);
  const localHintTeam = typeof localTeam === 'string' ? localTeam.toLowerCase() : null;
  const legendEntries = resolveRadarLegend(colorMode);
  const relevantHints = objectiveScoringHints
    .filter((hint) => !hint.team || !localHintTeam || (hint.team ?? '').toLowerCase() === localHintTeam)
    .filter((hint) => Boolean(hint.message) && Boolean(hint.routeZoneId));
  const activeHint = [...relevantHints].sort((left, right) => hintPriority(right) - hintPriority(left))[0] ?? null;
  const routeHintIds = new Set(
    [
      ...objectiveScoringHints.map((hint) => hint.routeZoneId),
      ctfMacro?.routeZoneId ?? null,
    ].filter((value): value is string => Boolean(value)),
  );
  const routeHintEntries: Array<readonly [string, string | null]> = objectiveScoringHints
    .filter((hint): hint is HudObjectiveScoringHint & { routeZoneId: string } => Boolean(hint.routeZoneId))
    .map((hint) => [hint.routeZoneId, normalizeText(hint.message, 30)] as const);
  if (ctfMacro?.routeZoneId) {
    routeHintEntries.push([ctfMacro.routeZoneId, normalizeText(ctfMacro.cue, 30) ?? normalizeText(ctfMacro.status, 30)]);
  }
  const routeHintMessages = new Map(routeHintEntries.filter((entry): entry is readonly [string, string] => Boolean(entry[0] && entry[1])));
  const routeOverlays: RouteOverlay[] = [];
  const tacticalCues: TacticalCue[] = [];
  const orderedKinds: RadarPoint['kind'][] = ['self', 'enemy', 'chaser', 'carrier', 'vehicle', 'deployable', 'ally', 'flag', 'base', 'generator-status', 'inventory', 'boost', 'route', 'ping'];
  const macroCue = ctfMacro ? ctfMacroCueText(ctfMacro) : null;

  const localFlag = flags.find((flag) => flag.team === localTeam);
  const localFlagHolderId = localFlag?.holderId ?? null;
  const localFlagCarriedByEnemy = localFlagHolderId != null;

  const friendlySensorBeacons = deployables.filter(
    (d) => d.active !== false && d.team === localTeam && d.deployableId === 'sensor-beacon' && d.position,
  );
  const sensorRange = 80;

  function isSensorDetected(enemyPos: Vec3Like | undefined): boolean {
    if (!enemyPos || friendlySensorBeacons.length === 0) return false;
    for (const sensor of friendlySensorBeacons) {
      const dx = (sensor.position?.x ?? 0) - (enemyPos.x ?? 0);
      const dz = (sensor.position?.z ?? 0) - (enemyPos.z ?? 0);
      if (Math.sqrt(dx * dx + dz * dz) <= sensorRange) return true;
    }
    return false;
  }

  points.push({
    id: 'self',
    x: 0,
    y: 0,
    kind: 'self',
    color: '#f4fbff',
    size: 10,
    stroke: 'rgba(98,221,255,0.7)',
    detail: 'YOU',
    threatPriority: threatPriority({ kind: 'self' }),
  });

  if (baseState) {
    const genOnline = baseState.generatorOnline !== false;
    const genHealth = baseState.generatorHealth ?? 1;
    points.push({
      id: 'generator-status',
      x: 0,
      y: -0.08,
      kind: 'generator-status',
      color: genOnline ? '#8ef0c9' : '#ff4444',
      size: 7,
      stroke: genOnline ? 'rgba(142,240,201,0.26)' : 'rgba(255,68,68,0.34)',
      detail: genOnline ? `GEN ${Math.round(genHealth * 100)}%` : 'GEN DOWN',
      label: genOnline ? 'G' : 'X',
      threatPriority: threatPriority({ kind: 'generator-status' }),
    });
  }

  for (const player of Object.values(remotePlayers)) {
    const point = localRelativePoint(localPosition, localYaw, player.position, RADAR_RADIUS);
    if (!point) {
      continue;
    }

    const sensorDetected = !localTeam || player.team !== localTeam ? isSensorDetected(player.position) : false;

    if (point.distance > radarRange && !sensorDetected) {
      continue;
    }

    const isAlly = localTeam != null && player.team === localTeam;
    const isCarrierContact = Boolean(player.carryingFlagTeam);
    const isEnemyCarrier = isCarrierContact && !isAlly;
    const isChaser = isAlly && localFlagCarriedByEnemy && localFlagHolderId !== player.id;
    const kind = isCarrierContact ? 'carrier' : isChaser ? 'chaser' : isAlly ? 'ally' : 'enemy';
    const tp = threatPriority({ kind, carryingFlagTeam: player.carryingFlagTeam, isEnemyCarrier });

    points.push({
      id: player.id ?? player.displayName ?? `player-${points.length}`,
      x: point.x,
      y: point.y,
      kind,
      color: isEnemyCarrier ? enemyColor : isCarrierContact ? '#ffcc66' : isChaser ? '#ff9b71' : isAlly ? allyColor : enemyColor,
      size: isEnemyCarrier ? 11 : isCarrierContact ? 9 : isChaser ? 8 : 7,
      stroke: isEnemyCarrier ? 'rgba(255,51,51,0.44)' : isCarrierContact ? 'rgba(255,204,102,0.34)' : isChaser ? 'rgba(255,155,113,0.32)' : isAlly ? 'rgba(98,221,255,0.26)' : 'rgba(255,155,113,0.24)',
      distance: point.distance,
      detail: isEnemyCarrier ? `ENEMY CARRY ${formatDistance(point.distance)}` : isCarrierContact ? `CARRY ${formatDistance(point.distance)}` : isChaser ? `CHASE ${formatDistance(point.distance)}` : formatDistance(point.distance),
      label: isEnemyCarrier ? 'EC' : isCarrierContact ? 'F' : isChaser ? 'CH' : sensorDetected && !isAlly ? 'S' : undefined,
      threatPriority: tp,
    });
  }

  for (const flag of flags) {
    const position = resolveFlagPosition(flag);
    const point = localRelativePoint(localPosition, localYaw, position ?? undefined, RADAR_RADIUS);
    if (!point) {
      continue;
    }

    if (point.distance > radarRange) {
      continue;
    }

    const isCarried = Boolean(flag.holderId);
    points.push({
      id: `flag-${flag.team ?? 'neutral'}-${flag.holderId ?? point.x}-${point.y}`,
      x: point.x,
      y: point.y,
      kind: isCarried ? 'carrier' : 'flag',
      color: teamColor(flag.team, colorMode),
      size: isCarried ? 9 : 8,
      stroke: isCarried ? 'rgba(255, 204, 102, 0.3)' : 'rgba(255,255,255,0.16)',
      distance: point.distance,
      detail: isCarried ? 'FLAG CARRIER' : 'FLAG',
      label: isCarried ? 'C' : 'F',
      threatPriority: threatPriority({ kind: isCarried ? 'carrier' : 'flag' }),
    });

    if (flag.homePosition) {
      const homePoint = localRelativePoint(localPosition, localYaw, flag.homePosition, RADAR_RADIUS);
      if (homePoint) {
        points.push({
          id: `base-${flag.team ?? 'neutral'}`,
          x: homePoint.x,
          y: homePoint.y,
          kind: 'base',
          color: teamColor(flag.team, colorMode),
          size: 10,
          stroke: 'rgba(255,255,255,0.18)',
          detail: 'BASE',
          distance: homePoint.distance,
          label: 'B',
          threatPriority: threatPriority({ kind: 'base' }),
        });
      }
    }
  }

  for (const pad of boostPads) {
    if (!pad.position) {
      continue;
    }

    const point = localRelativePoint(localPosition, localYaw, pad.position, RADAR_RADIUS);
    if (!point) {
      continue;
    }

    if (point.distance > radarRange) {
      continue;
    }

    const cooldownSeconds = pad.cooldownSeconds ?? 0;

    points.push({
      id: pad.id ?? `pad-${point.x}-${point.y}`,
      x: point.x,
      y: point.y,
      kind: 'boost',
      color: pad.active ? '#8ef0c9' : '#ffcc66',
      size: pad.active ? 6 : 5,
      stroke: pad.active ? 'rgba(142,240,201,0.25)' : 'rgba(255,204,102,0.22)',
      detail: pad.active ? 'BOOST' : cooldownSeconds > 0 ? `COOLDOWN ${Math.ceil(cooldownSeconds)}S` : 'COOLDOWN',
      label: pad.active ? undefined : pad.cooldownSeconds != null ? `${Math.max(0, Math.ceil(pad.cooldownSeconds))}` : undefined,
      distance: point.distance,
      threatPriority: threatPriority({ kind: 'boost' }),
    });
  }

  for (const station of inventoryStations) {
    if (!station.position || station.active === false || (localTeam && station.team !== localTeam)) {
      continue;
    }

    const point = localRelativePoint(localPosition, localYaw, station.position, RADAR_RADIUS);
    if (!point || point.distance > radarRange) {
      continue;
    }

    const powered = station.powered !== false;
    points.push({
      id: station.id ?? `inventory-${point.x}-${point.y}`,
      x: point.x,
      y: point.y,
      kind: 'inventory',
      color: powered ? '#8ef0c9' : '#ff8d74',
      size: powered ? 7 : 8,
      stroke: powered ? 'rgba(142,240,201,0.28)' : 'rgba(255,141,116,0.35)',
      detail: `${powered ? 'INVENTORY F1-F3' : 'INVENTORY OFFLINE'} ${formatDistance(point.distance)}`.trim(),
      label: powered ? 'I' : 'I!',
      distance: point.distance,
      threatPriority: threatPriority({ kind: 'inventory' }) + (powered ? 4 : 14),
    });
  }

  for (const vehicle of vehicles) {
    if (vehicle.active === false || !vehicle.position) {
      continue;
    }

    const point = localRelativePoint(localPosition, localYaw, vehicle.position, RADAR_RADIUS);
    if (!point || point.distance > radarRange) {
      continue;
    }

    const isAlly = localTeam != null && vehicle.team === localTeam;
    const occupied = Boolean(vehicle.occupiedBy);
    const isAirborne = vehicle.vehicleId === 'shrike';
    const altitude = vehicle.position?.y;
    const distanceLabel = formatDistance(point.distance);
    const actionHint = isAlly && !occupied && point.distance <= 8 ? ' PRESS V' : '';
    const occupancyLabel = occupied ? ' OCCUPIED' : '';
    const altitudeLabel =
      isAirborne && altitude != null && Number.isFinite(altitude) ? ` ALT ${Math.round(altitude)}m` : '';
    const returnLabel = !occupied && Number(vehicle.returnSeconds ?? 0) > 0
      ? ` RETURN ${Math.ceil(Number(vehicle.returnSeconds))}S`
      : '';
    points.push({
      id: vehicle.id ?? `vehicle-${point.x}-${point.y}`,
      x: point.x,
      y: point.y,
      kind: 'vehicle',
      color: isAirborne ? (isAlly ? '#9fe6ff' : '#ff8fc0') : isAlly ? '#ffcc66' : '#ff9b71',
      size: occupied ? 9 : 7,
      stroke: isAirborne
        ? isAlly
          ? 'rgba(159,230,255,0.32)'
          : 'rgba(255,143,192,0.34)'
        : isAlly
          ? 'rgba(255,204,102,0.3)'
          : 'rgba(255,155,113,0.32)',
      detail: `${isAlly ? 'ALLY' : 'ENEMY'} ${vehicleRadarName(vehicle.vehicleId)}${isAirborne ? ' AIRBORNE' : ''}${occupancyLabel}${actionHint}${returnLabel}${altitudeLabel} ${distanceLabel}`.trim(),
      label: (isAlly ? (occupied ? 'V+' : 'V') : occupied ? 'E+' : 'EV') + (isAirborne ? '▲' : ''),
      distance: point.distance,
      threatPriority: threatPriority({ kind: 'vehicle' }) + (isAlly ? 0 : 12) + (occupied ? 8 : 0) + (returnLabel ? 5 : 0),
      isAirborne,
    });
  }

  for (const deployable of deployables) {
    if (deployable.active === false || !deployable.position) {
      continue;
    }

    const point = localRelativePoint(localPosition, localYaw, deployable.position, RADAR_RADIUS);
    if (!point || point.distance > radarRange) {
      continue;
    }

    const isAlly = localTeam != null && deployable.team === localTeam;
    const kindLabel = (deployable.deployableId ?? 'deploy').replace(/-/g, ' ').toUpperCase();
    const deployableLabel =
      deployable.deployableId === 'shield-node'
        ? 'S'
        : deployable.deployableId === 'sensor-beacon'
          ? 'R'
          : deployable.deployableId === 'turret-small'
            ? 'T'
            : deployable.deployableId === 'turret-aa'
              ? '^'
              : deployable.deployableId === 'ammo-supply'
                ? 'A'
                : 'D';
    points.push({
      id: deployable.id ?? `deployable-${point.x}-${point.y}`,
      x: point.x,
      y: point.y,
      kind: 'deployable',
      color: isAlly ? '#b58cff' : '#ff8d74',
      size: isAlly ? 6 : 7,
      stroke: isAlly ? 'rgba(181,140,255,0.28)' : 'rgba(255,141,116,0.36)',
      detail: `${isAlly ? kindLabel : `ENEMY ${kindLabel}`} ${formatDistance(point.distance)}`.trim(),
      label: deployableLabel,
      distance: point.distance,
      ttlSeconds: deployable.ttlSeconds ?? undefined,
      threatPriority: threatPriority({ kind: 'deployable' }) + (isAlly ? 0 : 14),
    });
  }

  for (const ping of tacticalPings) {
    const point = localRelativePoint(localPosition, localYaw, ping.position, RADAR_RADIUS);
    if (!point) {
      continue;
    }

    if (point.distance > radarRange) {
      continue;
    }

    const pingTtl = ping.ttlSeconds ?? 0;
    const semantic = resolvePingSemantic(ping.kind);
    points.push({
      id: ping.id ?? `ping-${point.x}-${point.y}`,
      x: point.x,
      y: point.y,
      kind: 'ping',
      color: semantic.tone,
      size: 11,
      stroke: `${semantic.tone}66`,
      distance: point.distance,
      detail: shortPingCue(ping, point.distance),
      label: semantic.label,
      ttlSeconds: pingTtl,
      threatPriority: threatPriority({ kind: 'ping' }),
    });
    tacticalCues.push({
      id: `cue-ping-${ping.id ?? points.length}`,
      priority: pingPriority(ping),
      tone: semantic.tone,
      text: shortPingCue(ping, point.distance),
    });
  }

  for (const zone of routeCaptureZones) {
    const point = localRelativePoint(localPosition, localYaw, zone.position, RADAR_RADIUS);
    if (!point) {
      continue;
    }

    const radiusPct = Math.max(5, Math.min(22, ((zone.radius ?? 10) / RADAR_RADIUS) * RADAR_SCALE_PCT * 2.4));
    const semantic = resolveRouteZoneReadability({
      id: zone.id,
      ownerTeam: normalizeTeam(zone.ownerTeam),
      captureTargetTeam: normalizeTeam(zone.captureTargetTeam),
      captureProgress: zone.captureProgress ?? 0,
      contested: zone.contested ?? false,
      carrierInside: zone.carrierInside ?? false,
      scoringMultiplier: zone.scoringMultiplier ?? 1,
    });
    const overlay: RouteOverlay = {
      id: zone.id ?? `route-zone-${routeOverlays.length}`,
      x: point.x,
      y: point.y,
      radiusPct,
      captureProgress: Math.max(0, Math.min(1, zone.captureProgress ?? 0)),
      carrierInside: Boolean(zone.carrierInside),
      scoringMultiplier: zone.scoringMultiplier ?? 1,
      highlighted: routeHintIds.has(zone.id ?? ''),
      semantic,
      cueMessage: zone.id ? routeHintMessages.get(zone.id) ?? null : null,
    };
    routeOverlays.push(overlay);

    if (semantic.status !== 'neutral' || overlay.highlighted) {
      const routeLabel = routeStatusText(overlay);
      points.push({
        id: `route-point-${overlay.id}`,
        x: point.x,
        y: point.y,
        kind: 'route',
        color: routeOverlayTone(overlay.semantic, overlay),
        size: routeOverlaySize(overlay.semantic, overlay),
        stroke:
          overlay.semantic.status === 'contested'
            ? 'rgba(255,207,109,0.44)'
            : overlay.carrierInside || overlay.scoringMultiplier > 1.01
              ? 'rgba(255,204,102,0.34)'
              : overlay.semantic.status === 'threat'
                ? 'rgba(255,194,143,0.34)'
                : 'rgba(255,255,255,0.2)',
        detail: routeLabel,
        label: routeOverlayBadge(overlay.semantic, overlay),
        distance: point.distance,
        threatPriority: threatPriority({ kind: 'route' }),
      });
      tacticalCues.push({
        id: `cue-route-${overlay.id}`,
        priority: routePriority(overlay),
        tone: routeOverlayTone(overlay.semantic, overlay),
        text: `${routeLabel}${overlay.cueMessage ? ` · ${overlay.cueMessage.toUpperCase()}` : ''}`,
      });
    }
  }

  if (activeHint?.message) {
    tacticalCues.push({
      id: `cue-hint-${activeHint.id ?? 'active'}`,
      priority: hintPriority(activeHint) + 40,
      tone: hintTone(activeHint.kind, activeHint.team),
      text: normalizeText(activeHint.message, 38)?.toUpperCase() ?? 'OBJECTIVE UPDATE',
    });
  }

  const summary = contactSummary(points);
  const activeRoutes = routeOverlays.filter((overlay) => overlay.semantic.status !== 'neutral').length;
  const hotRoutes = routeOverlays.filter(
    (overlay) =>
      overlay.semantic.status === 'contested' ||
      overlay.semantic.status === 'threat' ||
      (overlay.semantic.status === 'hold' && (overlay.carrierInside || overlay.scoringMultiplier > 1.01)),
  ).length;
  const sortedPoints = [...points].sort((first, second) => {
    const priorityDiff = second.threatPriority - first.threatPriority;
    if (priorityDiff !== 0) {
      return priorityDiff;
    }
    const kindIndexA = orderedKinds.indexOf(first.kind);
    const kindIndexB = orderedKinds.indexOf(second.kind);
    if (kindIndexA !== kindIndexB) {
      return kindIndexA - kindIndexB;
    }
    return (first.distance ?? 0) - (second.distance ?? 0);
  });
  const contactCues: TacticalCue[] = sortedPoints
    .filter(shouldPromoteContactCue)
    .slice(0, minimal || narrow ? 0 : compact ? 2 : 3)
    .map((point) => ({
      id: `cue-contact-${point.id}`,
      priority: 240 + point.threatPriority,
      tone: point.color,
      text: normalizeText(point.detail, 42)?.toUpperCase() ?? point.kind.toUpperCase(),
    }));

  if (macroCue) {
    tacticalCues.push({
      id: `cue-ctf-${ctfMacro?.role ?? 'macro'}`,
      priority: 520,
      tone: ctfMacro?.tone ?? (colorMode === 'default' ? '#62ddff' : radarTone.blue),
      text: macroCue.toUpperCase(),
    });
  }

  const [mobileViewport, setMobileViewport] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 767px)');
    setMobileViewport(mq.matches);
    const handler = (e: MediaQueryListEvent) => setMobileViewport(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const radarFrameWidth = compact ? '100%' : mobileViewport ? Math.round(268 * 0.7) : 268;

  const effectiveRadarRange = sensorBoost ? Math.round(radarRange * 1.875) : radarRange;
  const statusText = narrow
    ? `R:${formatDistance(effectiveRadarRange)}  E:${summary.enemies}  A:${summary.allies}  V:${summary.vehicles}  I:${summary.inventory}  L:${hotRoutes}/${activeRoutes}`
      : `R:${formatDistance(effectiveRadarRange)}  H:${headingDegrees.toString().padStart(3, '0')}°  E:${summary.enemies}  A:${summary.allies}  C:${summary.carriers}  V:${summary.vehicles}  I:${summary.inventory}  D:${summary.deployables}  L:${hotRoutes}/${activeRoutes}`;
  const visibleCues = [...tacticalCues, ...contactCues]
    .sort((left, right) => right.priority - left.priority)
    .slice(0, minimal ? 0 : narrow ? 2 : 3);

  return (
    <HudFrame
      title={narrow ? 'RADAR' : 'TACTICAL RADAR'}
      subtitle={minimal ? undefined : statusText}
      tone="blue"
      dense={minimal || narrow}
      trailing={minimal ? undefined : (
        <div
          style={{
            fontSize: 10,
            color: 'rgba(231,244,252,0.66)',
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            padding: '1px 6px 2px',
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.04)',
          }}
        >
          live
        </div>
      )}
      style={{
        position: compact ? 'relative' : 'absolute',
        right: compact ? undefined : 14,
        bottom: compact ? undefined : 14,
        width: radarFrameWidth,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          position: 'relative',
          aspectRatio: '1 / 1',
          clipPath: frameClip(),
          WebkitClipPath: frameClip(),
          background:
            'radial-gradient(circle at center, rgba(98,221,255,0.12), rgba(7,13,22,0.94) 58%, rgba(4,7,12,0.99) 100%)',
          border: '1px solid rgba(98,221,255,0.14)',
          overflow: 'hidden',
          boxShadow: '0 0 0 1px rgba(255,255,255,0.04) inset, 0 0 20px rgba(0,0,0,0.22)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(45deg, rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(-45deg, rgba(255,255,255,0.018) 1px, transparent 1px)',
            backgroundSize: '30px 30px',
            maskImage: 'radial-gradient(circle at center, black 42%, transparent 100%)',
            opacity: 0.36,
          }}
        />
        {!narrow && !compact ? (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              display: 'grid',
              gap: 6,
              padding: '6px 8px',
              background: 'rgba(4, 9, 16, 0.54)',
              border: '1px solid rgba(255,255,255,0.08)',
              clipPath: frameClip(),
              WebkitClipPath: frameClip(),
              color: 'rgba(231,244,252,0.68)',
              fontSize: 9,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            {legendEntries.filter((entry) => entry.kind !== 'self').map((entry) => (
              <div key={entry.kind} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, display: 'inline-block', background: entry.tone, borderRadius: 2 }} />
                <span>{entry.label}</span>
              </div>
            ))}
          </div>
        ) : null}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: '82%',
            height: '82%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: '58%',
            height: '58%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 14,
            border: '1px solid rgba(255,255,255,0.06)',
            clipPath: 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)',
            opacity: 0.4,
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: '100%',
            height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.16), transparent)',
            transform: 'translateY(-50%)',
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 1,
            height: '100%',
            background: 'linear-gradient(180deg, transparent, rgba(255,255,255,0.16), transparent)',
            transform: 'translateX(-50%)',
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 10,
            right: 10,
            top: 10,
            bottom: 10,
            border: '1px solid rgba(255,255,255,0.05)',
            clipPath: 'polygon(0 0, 100% 0, 100% 18%, 82% 18%, 82% 82%, 100% 82%, 100% 100%, 0 100%, 0 82%, 18% 82%, 18% 18%, 0 18%)',
            opacity: 0.35,
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            top: 12,
            transform: 'translateX(-50%)',
            color: 'rgba(231,244,252,0.52)',
            fontSize: 10,
            letterSpacing: 1.6,
            textTransform: 'uppercase',
          }}
        >
          N
        </div>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'rgba(231,244,252,0.52)',
            fontSize: 10,
            letterSpacing: 1.6,
            textTransform: 'uppercase',
          }}
        >
          E
        </div>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 12,
            transform: 'translateX(-50%)',
            color: 'rgba(231,244,252,0.52)',
            fontSize: 10,
            letterSpacing: 1.6,
            textTransform: 'uppercase',
          }}
        >
          S
        </div>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'rgba(231,244,252,0.52)',
            fontSize: 10,
            letterSpacing: 1.6,
            textTransform: 'uppercase',
          }}
        >
          W
        </div>

        {routeOverlays.map((overlay) => {
          const left = `calc(50% + ${overlay.x * RADAR_SCALE_PCT}%)`;
          const top = `calc(50% + ${overlay.y * RADAR_SCALE_PCT}%)`;
          const tone = routeOverlayTone(overlay.semantic, overlay);
          const label = !narrow && shouldRenderRouteText(overlay) ? routeStatusText(overlay) : null;
          const ringOpacity =
            overlay.semantic.status === 'contested'
              ? 0.24
              : overlay.semantic.status === 'threat'
                ? 0.18
                : overlay.semantic.status === 'hold'
                  ? 0.12
                  : 0.08;
          return (
            <div
              key={`overlay-${overlay.id}`}
              style={{
                position: 'absolute',
                left,
                top,
                transform: 'translate(-50%, -50%)',
                width: `${overlay.radiusPct}%`,
                height: `${overlay.radiusPct}%`,
                borderRadius: '50%',
                border: `1px solid ${tone}${overlay.highlighted ? 'd6' : overlay.semantic.status === 'neutral' ? '44' : '88'}`,
                background:
                  overlay.semantic.status === 'contested'
                    ? 'rgba(255,207,109,0.16)'
                    : overlay.carrierInside || overlay.scoringMultiplier > 1.01
                      ? 'rgba(255,204,102,0.13)'
                      : `${tone}${overlay.highlighted ? '20' : '12'}`,
                boxShadow: overlay.highlighted ? `0 0 18px ${tone}66` : `0 0 10px ${tone}33`,
                opacity: overlay.semantic.status === 'neutral' ? 0.9 : 1,
                pointerEvents: 'none',
                zIndex: overlay.semantic.status === 'contested' ? 3 : overlay.semantic.status === 'threat' ? 2 : 1,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: `${Math.max(12, overlay.captureProgress * 100)}%`,
                  height: `${Math.max(12, overlay.captureProgress * 100)}%`,
                  transform: 'translate(-50%, -50%)',
                  borderRadius: '50%',
                  border: `1px solid ${tone}${overlay.highlighted ? 'aa' : '66'}`,
                  opacity: ringOpacity,
                }}
              />
              {overlay.semantic.status !== 'neutral' ? (
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: `${Math.max(18, overlay.radiusPct * 0.42)}%`,
                    height: `${Math.max(18, overlay.radiusPct * 0.42)}%`,
                    borderRadius: '50%',
                    border: `1px solid ${tone}55`,
                    opacity: 0.82,
                  }}
                />
              ) : null}
              {label ? (
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'rgba(231,244,252,0.78)',
                    fontSize: 8,
                    letterSpacing: 0.9,
                    textTransform: 'uppercase',
                    textShadow: '0 0 8px rgba(4,9,16,0.8)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </div>
              ) : null}
            </div>
          );
        })}

        {sortedPoints.map((point) => {
          const left = `calc(50% + ${point.x * RADAR_SCALE_PCT}%)`;
          const top = `calc(50% + ${point.y * RADAR_SCALE_PCT}%)`;
          const isSelf = point.kind === 'self';
          const isPing = point.kind === 'ping';
          const isAirborneVehicle = point.kind === 'vehicle' && point.isAirborne === true;
          const ageFactor = point.ttlSeconds == null ? 1 : Math.max(0.18, Math.min(1, point.ttlSeconds / 8));

          return (
            <div
              key={point.id}
              role="img"
              aria-label={point.detail ?? point.kind}
              style={{
                position: 'absolute',
                left,
                top,
                transform: 'translate(-50%, -50%)',
                zIndex:
                  point.kind === 'self'
                    ? 7
                    : point.kind === 'carrier' || point.kind === 'chaser'
                      ? 6
                      : point.kind === 'route'
                        ? 5
                        : point.kind === 'ping'
                          ? 4
                          : 3,
              }}
            >
              {isPing ? (
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: point.size * 2.8,
                    height: point.size * 2.8,
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '50%',
                    border: `1px solid ${point.color}88`,
                    opacity: ageFactor,
                    boxShadow: `0 0 12px ${point.color}77, inset 0 0 8px ${point.color}55`,
                    transition: reducedMotion ? undefined : 'opacity 100ms linear',
                  }}
                />
              ) : null}
              {isAirborneVehicle ? (
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: -Math.max(6, point.size * 0.7),
                    width: 0,
                    height: 0,
                    transform: 'translate(-50%, -100%)',
                    borderLeft: '3px solid transparent',
                    borderRight: '3px solid transparent',
                    borderBottom: `5px solid ${point.color}`,
                    filter: `drop-shadow(0 0 3px ${point.color}aa)`,
                  }}
                />
              ) : null}
              <div
                title={point.kind}
                style={{
                  position: 'relative',
                  width: point.size,
                  height: point.size,
                  clipPath: pointClip(point.kind),
                  WebkitClipPath: pointClip(point.kind),
                  background: point.color,
                  border: point.stroke ? `1px solid ${point.stroke}` : undefined,
                  boxShadow: isSelf
                    ? '0 0 0 3px rgba(244,251,255,0.06), 0 0 16px rgba(98,221,255,0.32)'
                    : point.kind === 'route'
                      ? `0 0 18px ${point.color}66`
                      : `0 0 14px ${point.color}66`,
                  display: 'grid',
                  placeItems: 'center',
                  color: '#08111d',
                  fontSize: 8,
                  fontWeight: 800,
                  letterSpacing: 0.5,
                  transform: reducedMotion
                    ? undefined
                    : point.kind === 'carrier' || point.kind === 'chaser'
                      ? 'scale(1.03)'
                      : point.kind === 'route'
                        ? 'scale(1.08)'
                        : undefined,
                }}
              >
                {point.label ?? ''}
              </div>
            </div>
          );
        })}
        {!minimal && visibleCues.length > 0 ? (
          <div
            data-radar-cue-strip="true"
            style={{
              position: 'absolute',
              left: 10,
              bottom: 10,
              display: 'grid',
              gap: 5,
              maxWidth: narrow ? '88%' : '70%',
              padding: '6px 8px',
              background: 'rgba(4, 9, 16, 0.56)',
              border: '1px solid rgba(255,255,255,0.08)',
              clipPath: frameClip(),
              WebkitClipPath: frameClip(),
            }}
          >
            {visibleCues.map((cue) => (
                <div
                  key={cue.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 9,
                    letterSpacing: 0.8,
                    textTransform: 'uppercase',
                    color: 'rgba(231,244,252,0.74)',
                    minWidth: 0,
                  }}
                >
                  <span style={{ width: 6, height: 6, background: cue.tone, flex: '0 0 auto' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cue.text}</span>
                </div>
              ))}
          </div>
        ) : null}
      </div>
    </HudFrame>
  );
}
